import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Plus, Users, Settings, Copy, Trash2, Edit, FileSpreadsheet } from 'lucide-react';
import { useToast } from '@/hooks/use-toast';
import ExcelGradeManager from '@/components/grades/ExcelGradeManager';

interface Class {
  id: number;
  class_name: string;
  subject: string;
  description: string;
  teacher_id: number;
  class_code: string;
  semester: string;
  is_active: boolean;
}

const ClassesManager: React.FC = () => {
  const [classes, setClasses] = useState<Class[]>([]);
  const [selectedClass, setSelectedClass] = useState<Class | null>(null);
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [showEditDialog, setShowEditDialog] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [newClass, setNewClass] = useState({
    class_name: '',
    subject: '',
    description: '',
    semester: '',
    is_active: true
  });
  const { toast } = useToast();

  useEffect(() => {
    loadClasses();
  }, []);

  const loadClasses = async () => {
    try {
      setIsLoading(true);
      // Get user info first
      const { data: userInfo, error: userError } = await window.ezsite.apis.getUserInfo();
      if (userError) throw userError;

      const { data, error } = await window.ezsite.apis.tablePage(15198, {
        PageNo: 1,
        PageSize: 100,
        Filters: [
        { name: 'teacher_id', op: 'Equal', value: userInfo.ID }],

        OrderByField: 'class_name',
        IsAsc: true
      });

      if (error) throw error;
      setClasses(data.List);
      console.log('Loaded classes:', data.List);
    } catch (error) {
      console.error('Error loading classes:', error);
      toast({
        title: 'Error',
        description: 'Failed to load classes',
        variant: 'destructive'
      });
    } finally {
      setIsLoading(false);
    }
  };

  const generateClassCode = () => {
    return Math.random().toString(36).substring(2, 8).toUpperCase();
  };

  const handleCreateClass = async () => {
    try {
      setIsLoading(true);
      // Get user info to get teacher_id
      const { data: userInfo, error: userError } = await window.ezsite.apis.getUserInfo();
      if (userError) throw userError;

      const classData = {
        ...newClass,
        teacher_id: userInfo.ID,
        class_code: generateClassCode()
      };

      const { error } = await window.ezsite.apis.tableCreate(15198, classData);
      if (error) throw error;

      toast({
        title: 'Success',
        description: 'Class created successfully!'
      });

      setShowCreateDialog(false);
      setNewClass({
        class_name: '',
        subject: '',
        description: '',
        semester: '',
        is_active: true
      });
      loadClasses();
    } catch (error) {
      console.error('Error creating class:', error);
      toast({
        title: 'Error',
        description: 'Failed to create class',
        variant: 'destructive'
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleUpdateClass = async () => {
    if (!selectedClass) return;

    try {
      setIsLoading(true);
      const { error } = await window.ezsite.apis.tableUpdate(15198, selectedClass);
      if (error) throw error;

      toast({
        title: 'Success',
        description: 'Class updated successfully!'
      });

      setShowEditDialog(false);
      loadClasses();
    } catch (error) {
      console.error('Error updating class:', error);
      toast({
        title: 'Error',
        description: 'Failed to update class',
        variant: 'destructive'
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleDeleteClass = async (classId: number) => {
    const confirmDelete = window.confirm(
      'Are you sure you want to delete this class? This action cannot be undone.'
    );

    if (!confirmDelete) return;

    try {
      setIsLoading(true);
      const { error } = await window.ezsite.apis.tableDelete(15198, { ID: classId });
      if (error) throw error;

      toast({
        title: 'Success',
        description: 'Class deleted successfully'
      });

      loadClasses();
    } catch (error) {
      console.error('Error deleting class:', error);
      toast({
        title: 'Error',
        description: 'Failed to delete class',
        variant: 'destructive'
      });
    } finally {
      setIsLoading(false);
    }
  };

  const copyClassCode = (classCode: string) => {
    navigator.clipboard.writeText(classCode);
    toast({
      title: 'Copied!',
      description: 'Class code copied to clipboard'
    });
  };

  return (
    <div className="space-y-6" data-id="2zy0k687b" data-path="src/components/classes/ClassesManager.tsx">
      <Card data-id="ti47rkmx0" data-path="src/components/classes/ClassesManager.tsx">
        <CardHeader data-id="4iz3ej3di" data-path="src/components/classes/ClassesManager.tsx">
          <div className="flex items-center justify-between" data-id="3hmo0o35l" data-path="src/components/classes/ClassesManager.tsx">
            <div data-id="znu0kjfxu" data-path="src/components/classes/ClassesManager.tsx">
              <CardTitle className="flex items-center gap-2" data-id="7g6dd5zf8" data-path="src/components/classes/ClassesManager.tsx">
                <Users className="h-5 w-5" data-id="38qg7hvqw" data-path="src/components/classes/ClassesManager.tsx" />
                My Classes
              </CardTitle>
              <CardDescription data-id="mitu44dwx" data-path="src/components/classes/ClassesManager.tsx">
                Manage your classes and course materials
              </CardDescription>
            </div>
            
            <Dialog open={showCreateDialog} onOpenChange={setShowCreateDialog} data-id="9vhyfin0m" data-path="src/components/classes/ClassesManager.tsx">
              <DialogTrigger asChild data-id="ox7rjxots" data-path="src/components/classes/ClassesManager.tsx">
                <Button data-id="hqtzwon79" data-path="src/components/classes/ClassesManager.tsx">
                  <Plus className="h-4 w-4 mr-2" data-id="209ql9s9d" data-path="src/components/classes/ClassesManager.tsx" />
                  Create Class
                </Button>
              </DialogTrigger>
              <DialogContent data-id="3bsxsv6xi" data-path="src/components/classes/ClassesManager.tsx">
                <DialogHeader data-id="cgpo5bdkz" data-path="src/components/classes/ClassesManager.tsx">
                  <DialogTitle data-id="9coyyqy6f" data-path="src/components/classes/ClassesManager.tsx">Create New Class</DialogTitle>
                  <DialogDescription data-id="yqu8dfw0k" data-path="src/components/classes/ClassesManager.tsx">
                    Set up a new class for your students
                  </DialogDescription>
                </DialogHeader>
                <div className="space-y-4" data-id="cn5cos9bs" data-path="src/components/classes/ClassesManager.tsx">
                  <div data-id="g79ky75zl" data-path="src/components/classes/ClassesManager.tsx">
                    <Label htmlFor="class_name" data-id="wby7527ub" data-path="src/components/classes/ClassesManager.tsx">Class Name</Label>
                    <Input
                      id="class_name"
                      value={newClass.class_name}
                      onChange={(e) => setNewClass({ ...newClass, class_name: e.target.value })}
                      placeholder="e.g., Math 101" data-id="awbqg1hfl" data-path="src/components/classes/ClassesManager.tsx" />

                  </div>
                  <div data-id="8kmqunzb4" data-path="src/components/classes/ClassesManager.tsx">
                    <Label htmlFor="subject" data-id="t6u16kvnr" data-path="src/components/classes/ClassesManager.tsx">Subject</Label>
                    <Input
                      id="subject"
                      value={newClass.subject}
                      onChange={(e) => setNewClass({ ...newClass, subject: e.target.value })}
                      placeholder="e.g., Mathematics" data-id="82316ka1a" data-path="src/components/classes/ClassesManager.tsx" />

                  </div>
                  <div data-id="l45wffwck" data-path="src/components/classes/ClassesManager.tsx">
                    <Label htmlFor="semester" data-id="pbdyf7ryv" data-path="src/components/classes/ClassesManager.tsx">Semester</Label>
                    <Input
                      id="semester"
                      value={newClass.semester}
                      onChange={(e) => setNewClass({ ...newClass, semester: e.target.value })}
                      placeholder="e.g., Fall 2024" data-id="6assxjmm5" data-path="src/components/classes/ClassesManager.tsx" />

                  </div>
                  <div data-id="eq9ti3dwq" data-path="src/components/classes/ClassesManager.tsx">
                    <Label htmlFor="description" data-id="6ck3z0ws5" data-path="src/components/classes/ClassesManager.tsx">Description</Label>
                    <Textarea
                      id="description"
                      value={newClass.description}
                      onChange={(e) => setNewClass({ ...newClass, description: e.target.value })}
                      placeholder="Brief description of the class" data-id="tkvh9xefd" data-path="src/components/classes/ClassesManager.tsx" />

                  </div>
                  <div className="flex justify-end gap-2" data-id="ox9kf4a5p" data-path="src/components/classes/ClassesManager.tsx">
                    <Button variant="outline" onClick={() => setShowCreateDialog(false)} data-id="9ol4dbp88" data-path="src/components/classes/ClassesManager.tsx">
                      Cancel
                    </Button>
                    <Button onClick={handleCreateClass} disabled={isLoading} data-id="aiviynseo" data-path="src/components/classes/ClassesManager.tsx">
                      {isLoading ? 'Creating...' : 'Create Class'}
                    </Button>
                  </div>
                </div>
              </DialogContent>
            </Dialog>
          </div>
        </CardHeader>
      </Card>

      {classes.length === 0 ?
      <Card data-id="2pk510n7y" data-path="src/components/classes/ClassesManager.tsx">
          <CardContent className="text-center py-12" data-id="1aqks50t5" data-path="src/components/classes/ClassesManager.tsx">
            <Users className="mx-auto h-12 w-12 text-gray-400 mb-4" data-id="s8iwujrbh" data-path="src/components/classes/ClassesManager.tsx" />
            <h3 className="text-lg font-medium mb-2" data-id="mqkt0hwvm" data-path="src/components/classes/ClassesManager.tsx">No Classes Yet</h3>
            <p className="text-gray-600 mb-4" data-id="at31kcnl5" data-path="src/components/classes/ClassesManager.tsx">
              Create your first class to start teaching and managing students
            </p>
            <Button onClick={() => setShowCreateDialog(true)} data-id="2q7d5aeq4" data-path="src/components/classes/ClassesManager.tsx">
              <Plus className="h-4 w-4 mr-2" data-id="8nir1a5o9" data-path="src/components/classes/ClassesManager.tsx" />
              Create Your First Class
            </Button>
          </CardContent>
        </Card> :

      <Tabs defaultValue="overview" className="w-full" data-id="sujxnt983" data-path="src/components/classes/ClassesManager.tsx">
          <TabsList data-id="mpgj60trr" data-path="src/components/classes/ClassesManager.tsx">
            <TabsTrigger value="overview" data-id="m65ua9ymy" data-path="src/components/classes/ClassesManager.tsx">Class Overview</TabsTrigger>
            {selectedClass &&
          <>
                <TabsTrigger value="grades" data-id="rx7abtr3f" data-path="src/components/classes/ClassesManager.tsx">
                  Grade Sheets - {selectedClass.class_name}
                </TabsTrigger>
              </>
          }
          </TabsList>

          <TabsContent value="overview" className="space-y-4" data-id="iuya0le30" data-path="src/components/classes/ClassesManager.tsx">
            <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3" data-id="0nb4xaxhk" data-path="src/components/classes/ClassesManager.tsx">
              {classes.map((classItem) =>
            <Card key={classItem.id} className="hover:shadow-md transition-shadow" data-id="pra19s11j" data-path="src/components/classes/ClassesManager.tsx">
                  <CardHeader data-id="2nc1vo0vh" data-path="src/components/classes/ClassesManager.tsx">
                    <div className="flex items-center justify-between" data-id="7wwqs7k2o" data-path="src/components/classes/ClassesManager.tsx">
                      <CardTitle className="text-lg" data-id="c86fx3c64" data-path="src/components/classes/ClassesManager.tsx">{classItem.class_name}</CardTitle>
                      <Badge variant={classItem.is_active ? 'default' : 'secondary'} data-id="wrbjcn30q" data-path="src/components/classes/ClassesManager.tsx">
                        {classItem.is_active ? 'Active' : 'Inactive'}
                      </Badge>
                    </div>
                    <CardDescription data-id="2qr3vvr22" data-path="src/components/classes/ClassesManager.tsx">{classItem.subject}</CardDescription>
                  </CardHeader>
                  <CardContent data-id="zjkh6rriy" data-path="src/components/classes/ClassesManager.tsx">
                    <div className="space-y-3" data-id="sd3hhpg1k" data-path="src/components/classes/ClassesManager.tsx">
                      <p className="text-sm text-gray-600" data-id="831dbun0u" data-path="src/components/classes/ClassesManager.tsx">{classItem.description}</p>
                      <div className="flex items-center justify-between" data-id="q2mwaduy5" data-path="src/components/classes/ClassesManager.tsx">
                        <span className="text-sm font-medium" data-id="1l1eon5ch" data-path="src/components/classes/ClassesManager.tsx">Class Code:</span>
                        <div className="flex items-center gap-2" data-id="at2oiqfpk" data-path="src/components/classes/ClassesManager.tsx">
                          <code className="bg-gray-100 px-2 py-1 rounded text-sm font-mono" data-id="1jehfz0f0" data-path="src/components/classes/ClassesManager.tsx">
                            {classItem.class_code}
                          </code>
                          <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => copyClassCode(classItem.class_code)} data-id="ca73zi8v4" data-path="src/components/classes/ClassesManager.tsx">

                            <Copy className="h-3 w-3" data-id="o1ev6wr1o" data-path="src/components/classes/ClassesManager.tsx" />
                          </Button>
                        </div>
                      </div>
                      <div className="flex items-center justify-between" data-id="793mmaxe4" data-path="src/components/classes/ClassesManager.tsx">
                        <span className="text-sm text-gray-600" data-id="oq2xhzzhb" data-path="src/components/classes/ClassesManager.tsx">Semester:</span>
                        <span className="text-sm font-medium" data-id="twl8c72zi" data-path="src/components/classes/ClassesManager.tsx">{classItem.semester}</span>
                      </div>
                      
                      <div className="flex gap-2 pt-3" data-id="o1atljaf7" data-path="src/components/classes/ClassesManager.tsx">
                        <Button
                      variant="outline"
                      size="sm"
                      onClick={() => {
                        setSelectedClass(classItem);
                        setShowEditDialog(true);
                      }} data-id="0ok6soxdd" data-path="src/components/classes/ClassesManager.tsx">

                          <Edit className="h-4 w-4 mr-1" data-id="y909smtl0" data-path="src/components/classes/ClassesManager.tsx" />
                          Edit
                        </Button>
                        <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setSelectedClass(classItem)} data-id="j7hyvtbg4" data-path="src/components/classes/ClassesManager.tsx">

                          <FileSpreadsheet className="h-4 w-4 mr-1" data-id="mzmyy8o34" data-path="src/components/classes/ClassesManager.tsx" />
                          Grades
                        </Button>
                        <Button
                      variant="outline"
                      size="sm"
                      onClick={() => handleDeleteClass(classItem.id)}
                      className="text-red-600 hover:text-red-700" data-id="b91v67o4a" data-path="src/components/classes/ClassesManager.tsx">

                          <Trash2 className="h-4 w-4" data-id="iwd9068ca" data-path="src/components/classes/ClassesManager.tsx" />
                        </Button>
                      </div>
                    </div>
                  </CardContent>
                </Card>
            )}
            </div>
          </TabsContent>

          <TabsContent value="grades" data-id="0dddif995" data-path="src/components/classes/ClassesManager.tsx">
            {selectedClass ?
          <ExcelGradeManager
            classId={selectedClass.id}
            className={selectedClass.class_name} data-id="l6gwqkegu" data-path="src/components/classes/ClassesManager.tsx" /> :


          <Card data-id="xhhje1sst" data-path="src/components/classes/ClassesManager.tsx">
                <CardContent className="text-center py-12" data-id="0lv69cf8i" data-path="src/components/classes/ClassesManager.tsx">
                  <p className="text-gray-600" data-id="yhrnyafbk" data-path="src/components/classes/ClassesManager.tsx">Select a class to manage grade sheets</p>
                </CardContent>
              </Card>
          }
          </TabsContent>
        </Tabs>
      }

      {/* Edit Class Dialog */}
      <Dialog open={showEditDialog} onOpenChange={setShowEditDialog} data-id="p6w8vxk9e" data-path="src/components/classes/ClassesManager.tsx">
        <DialogContent data-id="4eynmo98u" data-path="src/components/classes/ClassesManager.tsx">
          <DialogHeader data-id="psjnwj358" data-path="src/components/classes/ClassesManager.tsx">
            <DialogTitle data-id="qj61m1dgh" data-path="src/components/classes/ClassesManager.tsx">Edit Class</DialogTitle>
            <DialogDescription data-id="oubut9non" data-path="src/components/classes/ClassesManager.tsx">
              Update your class information
            </DialogDescription>
          </DialogHeader>
          {selectedClass &&
          <div className="space-y-4" data-id="68l0wmc8p" data-path="src/components/classes/ClassesManager.tsx">
              <div data-id="yg5inchej" data-path="src/components/classes/ClassesManager.tsx">
                <Label htmlFor="edit_class_name" data-id="unh0ktkd2" data-path="src/components/classes/ClassesManager.tsx">Class Name</Label>
                <Input
                id="edit_class_name"
                value={selectedClass.class_name}
                onChange={(e) => setSelectedClass({ ...selectedClass, class_name: e.target.value })} data-id="1kdp8qu71" data-path="src/components/classes/ClassesManager.tsx" />

              </div>
              <div data-id="uerrgshkf" data-path="src/components/classes/ClassesManager.tsx">
                <Label htmlFor="edit_subject" data-id="xeiuetr6t" data-path="src/components/classes/ClassesManager.tsx">Subject</Label>
                <Input
                id="edit_subject"
                value={selectedClass.subject}
                onChange={(e) => setSelectedClass({ ...selectedClass, subject: e.target.value })} data-id="ncge8e0cy" data-path="src/components/classes/ClassesManager.tsx" />

              </div>
              <div data-id="oxibid4hv" data-path="src/components/classes/ClassesManager.tsx">
                <Label htmlFor="edit_semester" data-id="yuc9hb2bi" data-path="src/components/classes/ClassesManager.tsx">Semester</Label>
                <Input
                id="edit_semester"
                value={selectedClass.semester}
                onChange={(e) => setSelectedClass({ ...selectedClass, semester: e.target.value })} data-id="3kv6uc29d" data-path="src/components/classes/ClassesManager.tsx" />

              </div>
              <div data-id="asukiodwt" data-path="src/components/classes/ClassesManager.tsx">
                <Label htmlFor="edit_description" data-id="nqe1vuj5p" data-path="src/components/classes/ClassesManager.tsx">Description</Label>
                <Textarea
                id="edit_description"
                value={selectedClass.description}
                onChange={(e) => setSelectedClass({ ...selectedClass, description: e.target.value })} data-id="ui6xi8ruz" data-path="src/components/classes/ClassesManager.tsx" />

              </div>
              <div className="flex justify-end gap-2" data-id="5ts1wrhcp" data-path="src/components/classes/ClassesManager.tsx">
                <Button variant="outline" onClick={() => setShowEditDialog(false)} data-id="gmjn0m0el" data-path="src/components/classes/ClassesManager.tsx">
                  Cancel
                </Button>
                <Button onClick={handleUpdateClass} disabled={isLoading} data-id="za1q705u4" data-path="src/components/classes/ClassesManager.tsx">
                  {isLoading ? 'Updating...' : 'Update Class'}
                </Button>
              </div>
            </div>
          }
        </DialogContent>
      </Dialog>
    </div>);

};

export default ClassesManager;